import React, { ChangeEvent, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Button, Input, InputNumber, notification } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import api from '../../../api'
import { getPortalPreviewTargetOrigin } from '../helpers/preview-url'
import { PortalEditorField } from './PortalEditorField'

interface PortalImagePathFieldValue {
  url?: string
  alt?: string
  width?: number
  height?: number
}

interface PortalImagePathFieldProps {
  value: PortalImagePathFieldValue
  onChange: (value: PortalImagePathFieldValue) => void
  urlPlaceholder?: string
  urlInputId?: string
  altInputId?: string
  showDimensions?: boolean
}

const PORTAL_IMAGE_PATH_PREFIX = '/images/portal/'
const MAX_IMAGE_SIZE = 5 * 1024 * 1024
const ACCEPTED_IMAGE_TYPES = '.jpg,.jpeg,.png,.webp,.avif,image/jpeg,image/png,image/webp,image/avif'
// O upload permanece oculto no CMS enquanto o fluxo ainda não estiver finalizado.
const IMAGE_UPLOAD_ENABLED = false

export function PortalImagePathField({
  value,
  onChange,
  urlPlaceholder = '/images/portal/imagem.png',
  urlInputId,
  altInputId,
  showDimensions = true,
}: PortalImagePathFieldProps) {
  const [imageFailed, setImageFailed] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const valueRef = useRef(value)
  valueRef.current = value
  const previewSrc = useMemo(() => resolveImagePreviewSrc(value.url), [value.url])

  const update = (patch: Partial<PortalImagePathFieldValue>) => {
    onChange({ ...value, ...patch })
  }

  const applyPortalPathPrefix = () => {
    if (value.url?.startsWith(PORTAL_IMAGE_PATH_PREFIX)) return
    update({ url: PORTAL_IMAGE_PATH_PREFIX })
  }

  const uploadImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    event.currentTarget.value = ''
    if (!file || uploading) return

    if (file.size > MAX_IMAGE_SIZE) {
      notification.error({ message: 'A imagem deve possuir no máximo 5 MB.' })
      return
    }

    setUploading(true)
    try {
      const uploaded = await api.PortalArtigos.uploadImage(file)
      const currentValue = valueRef.current
      setImageFailed(false)
      onChange({
        ...currentValue,
        url: uploaded.url,
        ...(showDimensions ? { width: uploaded.width, height: uploaded.height } : {}),
      })
    } catch (error) {
      notification.error({
        message:
          error instanceof Error
            ? error.message
            : 'Não foi possível enviar a imagem. Tente novamente.',
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <FieldLayout>
      {IMAGE_UPLOAD_ENABLED && (
        <UploadActions>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            hidden
            aria-label="Selecionar imagem do Portal"
            onChange={uploadImage}
          />
          <Button
            type="default"
            size="small"
            icon={<UploadOutlined />}
            loading={uploading}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Enviar imagem
          </Button>
          <UploadHint>JPEG, PNG, WebP ou AVIF. Máximo de 5 MB.</UploadHint>
        </UploadActions>
      )}
      <PortalEditorField label="URL/path da imagem">
        <Input
          id={urlInputId}
          value={value.url}
          placeholder={urlPlaceholder}
          onChange={(event) => {
            setImageFailed(false)
            update({ url: event.target.value })
          }}
        />
      </PortalEditorField>
      <Button size="small" onClick={applyPortalPathPrefix}>
        Usar /images/portal/
      </Button>
      <PortalEditorField label="Texto alternativo">
        <Input
          id={altInputId}
          value={value.alt}
          placeholder="Texto alternativo"
          onChange={(event) => update({ alt: event.target.value })}
        />
      </PortalEditorField>
      {showDimensions && (
        <DimensionsGrid>
          <PortalEditorField label="Largura">
            <InputNumber
              value={value.width}
              min={1}
              placeholder="Largura"
              style={{ width: '100%' }}
              onChange={(width) => update({ width: Number(width || 0) })}
            />
          </PortalEditorField>
          <PortalEditorField label="Altura">
            <InputNumber
              value={value.height}
              min={1}
              placeholder="Altura"
              style={{ width: '100%' }}
              onChange={(height) => update({ height: Number(height || 0) })}
            />
          </PortalEditorField>
        </DimensionsGrid>
      )}

      {previewSrc && (
        <PreviewFrame>
          {!imageFailed ? (
            <img
              src={previewSrc}
              alt={value.alt || 'Preview da imagem'}
              onError={() => setImageFailed(true)}
            />
          ) : (
            <PreviewError>Não foi possível carregar a imagem.</PreviewError>
          )}
        </PreviewFrame>
      )}
    </FieldLayout>
  )
}

function resolveImagePreviewSrc(url?: string) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith('/')) return `${getPortalPreviewTargetOrigin()}${url}`
  return url
}

const FieldLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const UploadActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
`

const UploadHint = styled.span`
  color: #8fa0b5;
  font-size: 12px;
`

const DimensionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`

const PreviewFrame = styled.div`
  width: 100%;
  border: 1px solid #e1e4ed;
  border-radius: 6px;
  overflow: hidden;
  background: #f9fafb;

  img {
    display: block;
    width: 100%;
    max-height: 220px;
    object-fit: contain;
  }
`

const PreviewError = styled.div`
  padding: 16px;
  color: #8fa0b5;
  text-align: center;
`
